import * as Dialog from '@radix-ui/react-dialog';
import clsx from 'clsx';
import { useTranslations } from 'next-intl';
import type { ReactNode } from 'react';

import BaseContainer, { type BaseContainerProps } from '@commons/containers/BaseContainer';
import { API_WEBSITE } from '@commons/utils/constants';

import IconButton from '../IconButton';
import Spacer from '../Spacer';
import styles from './index.module.scss';

/**
 * Get the default theme for the drawer based on the current website.
 */

const getDefaultDrawerTheme = () => {
  switch (API_WEBSITE) {
    case 'crisis24':
      return 'grey-11';
    case 'codered':
      return 'black';
    case 'aiia':
      return 'grey-11';
    default:
      return 'black';
  }
};

/**
 * Type definition for the Drawer component properties.
 */

export type DrawerProps = Dialog.DialogProps & {
  trigger?: ReactNode;
  triggerProps?: Dialog.DialogTriggerProps;
  theme?: BaseContainerProps['theme'];
  width?: BaseContainerProps['width'];
  padding?: BaseContainerProps['padding'];
  backgroundColor?: BaseContainerProps['backgroundColor'];
};

/**
 * Drawer component (based on Radix Dialog).
 *
 * The `Drawer.Title` and `Drawer.Description` subcomponents should be used to automatically manage screen reader accessibility.
 *
 * @see https://www.radix-ui.com/primitives/docs/components/dialog
 *
 * IMPORTANT:
 * Do not use the 'use client' directive for this component, as it's used on the server in multiple places.
 * If interactivity is needed, consider composing this component with a client component separately.
 */

export const Drawer = (props: DrawerProps) => {
  const t = useTranslations('commons');

  const {
    theme = getDefaultDrawerTheme(),
    width = { xs: 12, md: 10, lg: 8, xl: 6 },
    padding = 'lg',
    trigger,
    triggerProps = { asChild: true },
    backgroundColor,
    modal = true,
    children,
    ...rest
  } = props;

  // Ignore clicks on 3rd party widgets like CookiePro.
  const handleClickOutside: Dialog.DialogContentProps['onPointerDownOutside'] = (event) => {
    const target = event.target as HTMLElement;

    if (target.closest('.ot-sdk-container')) {
      event.preventDefault();
    }
  };

  return (
    <Dialog.Root modal={modal} {...rest}>
      {trigger && <Dialog.Trigger {...triggerProps}>{trigger}</Dialog.Trigger>}
      <Dialog.Portal>
        <Dialog.Overlay className={styles.overlay} />
        <Dialog.Content
          aria-modal={modal || undefined}
          onPointerDownOutside={handleClickOutside}
          asChild
        >
          <BaseContainer
            theme={theme}
            width={width}
            padding={padding}
            backgroundColor={backgroundColor}
            className={clsx(styles.drawer, 'drawer')}
          >
            <BaseContainer className="h-100">
              {children}
              <Spacer size="lg" />
              <Dialog.Close asChild>
                <IconButton
                  type="close"
                  aria-label={t('close')}
                  colors={{ icon: 'white', bg: 'grey-9' }}
                  activeColors={{ icon: 'black', bg: 'white' }}
                  className={styles.closeBtn}
                />
              </Dialog.Close>
            </BaseContainer>
          </BaseContainer>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};

/**
 * Drawer title component.
 *
 * Manages screen reader accessibility.
 *
 * @see https://www.radix-ui.com/primitives/docs/components/dialog#title
 */

Drawer.Title = Dialog.Title;

/**
 * Drawer description component.
 *
 * Manages screen reader accessibility.
 *
 * @see https://www.radix-ui.com/primitives/docs/components/dialog#description
 */

Drawer.Description = Dialog.Description;

export default Drawer;
